import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from 'services/api';
import useAuthStore from 'stores/authStore';
import { Button } from 'components/ui/button';
import { Card, CardContent } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import {
  ArrowLeft, Users, Clock, MessageCircle, X, Check, Loader2, UserPlus, Inbox
} from 'lucide-react';
import { toast } from 'sonner';

export default function ClientDirectory() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [loading, setLoading] = useState(true);
  const [incomingRequests, setIncomingRequests] = useState([]);
  const [myClients, setMyClients] = useState([]);
  const [respondingTo, setRespondingTo] = useState(null);

  useEffect(() => {
    if (user?.role !== 'independent_caregiver') {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await api.get('/caregivers/requests/received');
      const requests = response.data.requests || [];
      setMyClients(requests.filter(r => r.status === 'accepted'));
      setIncomingRequests(requests.filter(r => r.status === 'pending'));
    } catch (error) {
      console.error('Failed to fetch requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRespondToRequest = async (requestId, action) => {
    setRespondingTo(requestId);
    try {
      await api.put(`/caregivers/requests/${requestId}/respond`, { action });
      toast.success(action === 'accept' ? 'Request accepted!' : 'Request declined');
      const req = incomingRequests.find(r => r.request_id === requestId);
      if (action === 'accept' && req) {
        setMyClients(prev => [...prev, { ...req, status: 'accepted' }]);
      }
      setIncomingRequests(prev => prev.filter(r => r.request_id !== requestId));
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action}`);
    } finally {
      setRespondingTo(null);
    }
  };

  const isEmpty = !loading && myClients.length === 0 && incomingRequests.length === 0;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-slate-900 font-['Manrope']">Client Requests</h1>
              <p className="text-xs sm:text-sm text-slate-500">Manage your clients and incoming requests</p>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-slate-200 rounded-full" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-slate-200 rounded w-1/3" />
                      <div className="h-3 bg-slate-200 rounded w-1/2" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <>
            {/* Active Requests */}
            {incomingRequests.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Inbox className="w-5 h-5 text-teal-500" />
                  <h2 className="text-base font-semibold text-slate-900">
                    Active Requests
                    <Badge className="ml-2 bg-teal-500">{incomingRequests.length}</Badge>
                  </h2>
                </div>
                <div className="space-y-3">
                  {incomingRequests.map((req) => (
                    <Card key={req.request_id} className="border-teal-200 bg-teal-50/30">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center shrink-0">
                            <UserPlus className="w-5 h-5 text-teal-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-slate-900 text-sm sm:text-base">{req.family_name}</h3>
                            {req.message && (
                              <p className="text-sm text-slate-600 mt-0.5 line-clamp-2">{req.message}</p>
                            )}
                            {req.preferred_schedule && (
                              <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {req.preferred_schedule}
                              </p>
                            )}
                            <div className="flex items-center gap-2 mt-3">
                              <Button
                                size="sm"
                                onClick={() => handleRespondToRequest(req.request_id, 'accept')}
                                disabled={respondingTo === req.request_id}
                                className="bg-teal-500 hover:bg-teal-600 h-8 text-xs"
                              >
                                {respondingTo === req.request_id
                                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  : <Check className="w-3.5 h-3.5" />}
                                Accept
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleRespondToRequest(req.request_id, 'decline')}
                                disabled={respondingTo === req.request_id}
                                className="border-red-200 text-red-600 hover:bg-red-50 h-8 text-xs"
                              >
                                <X className="w-3.5 h-3.5" />
                                Decline
                              </Button>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* My Clients */}
            {myClients.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-5 h-5 text-teal-600" />
                  <h2 className="text-base font-semibold text-slate-900">
                    My Clients
                    <Badge className="ml-2 bg-teal-500">{myClients.length}</Badge>
                  </h2>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {myClients.map((client) => (
                    <Card key={client.request_id} className="border-teal-200">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center shrink-0 text-teal-700 font-bold">
                            {client.family_name?.[0] || '?'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-slate-900 text-sm truncate">{client.family_name}</h3>
                            {client.preferred_schedule && (
                              <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                                <Clock className="w-3 h-3" />
                                {client.preferred_schedule}
                              </p>
                            )}
                            {client.message && (
                              <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{client.message}</p>
                            )}
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(`/messages?chat_with=${client.family_id}`)}
                            className="shrink-0 h-8 text-xs border-teal-200 text-teal-700 hover:bg-teal-50"
                          >
                            <MessageCircle className="w-3.5 h-3.5" />
                            Message
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Empty State */}
            {isEmpty && (
              <Card>
                <CardContent className="py-16 text-center">
                  <Inbox className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <h3 className="text-lg font-medium text-slate-900 mb-2">No requests yet</h3>
                  <p className="text-slate-500 max-w-sm mx-auto">
                    When families send you care requests, they will appear here. Make sure your profile is complete so families can find you.
                  </p>
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => navigate('/settings')}
                  >
                    Complete Your Profile
                  </Button>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
}
